import { useState, useEffect, useCallback } from 'react';
import { Smartphone, Loader, CheckCircle, XCircle, CreditCard, Wifi, WifiOff } from 'lucide-react';
import { supabase } from './supabase';
import { calculatePlatformFee, formatCurrency } from './stripe';
import { useModal } from './AppModal';

export default function TerminalPaymentButton({
  invoice,
  companyId,
  connectedAccountId,
  onSuccess,
  size = 'normal', // 'normal' or 'large'
}) {
  const { showError, showSuccess } = useModal();
  const [status, setStatus] = useState('idle'); // idle, connecting, collecting, processing, succeeded, failed
  const [terminal, setTerminal] = useState(null);
  const [connectedReader, setConnectedReader] = useState(null);
  const [readers, setReaders] = useState([]);
  const [loadingReaders, setLoadingReaders] = useState(true);
  const [error, setError] = useState(null);
  const [showReaderSelect, setShowReaderSelect] = useState(false);

  // Load available readers
  useEffect(() => {
    if (companyId) {
      loadReaders();
    }
  }, [companyId]);

  const loadReaders = async () => {
    try {
      setLoadingReaders(true);
      const response = await fetch(`/api/terminal?action=list-readers&companyId=${companyId}`);
      const data = await response.json();

      if (response.ok) {
        setReaders(data.readers || []);
      }
    } catch (err) {
    } finally {
      setLoadingReaders(false);
    }
  };

  // Initialize Terminal SDK
  const initializeTerminal = useCallback(async () => {
    if (terminal) return terminal;

    try {
      // Load Stripe Terminal SDK if not loaded
      if (!window.StripeTerminal) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://js.stripe.com/terminal/v1/';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      const terminalInstance = window.StripeTerminal.create({
        onFetchConnectionToken: async () => {
          const response = await fetch('/api/terminal?action=create-connection-token', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ companyId }),
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.error);
          return data.secret;
        },
        onUnexpectedReaderDisconnect: () => {
          setConnectedReader(null);
          setStatus('idle');
          showError('Reader disconnected unexpectedly');
        },
      });

      setTerminal(terminalInstance);
      return terminalInstance;
    } catch (err) {
      throw err;
    }
  }, [terminal, companyId]);

  // Connect to a specific reader
  const connectToReader = async (reader) => {
    try {
      setStatus('connecting');
      setError(null);

      const term = await initializeTerminal();

      // For simulated reader, use simulated mode
      const isSimulated = reader.device_type === 'simulated' || reader.id?.includes('simulated');

      let discoveredReaders;
      if (isSimulated) {
        const result = await term.discoverReaders({ simulated: true });
        discoveredReaders = result.discoveredReaders;
      } else {
        const result = await term.discoverReaders({ simulated: false });
        discoveredReaders = result.discoveredReaders;
      }

      if (!discoveredReaders || discoveredReaders.length === 0) {
        throw new Error('No readers found. Make sure the reader is powered on and nearby.');
      }

      // Find matching reader
      const targetReader = discoveredReaders.find(r => r.id === reader.id) || discoveredReaders[0];

      const { reader: connected, error: connectError } = await term.connectReader(targetReader);

      if (connectError) {
        throw new Error(connectError.message);
      }

      setConnectedReader(connected);
      setStatus('idle');
      setShowReaderSelect(false);

      return connected;
    } catch (err) {
      setError(err.message);
      setStatus('failed');
      throw err;
    }
  };

  // Collect payment
  const collectPayment = async () => {
    if (!connectedReader) {
      setShowReaderSelect(true);
      return;
    }

    try {
      setStatus('collecting');
      setError(null);

      const amountCents = Math.round(invoice.balance_due * 100);
      const platformFeeCents = calculatePlatformFee(amountCents, 'terminal');

      // Create PaymentIntent
      const createResponse = await fetch('/api/terminal?action=create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId,
          invoiceId: invoice.id,
          amountCents,
          platformFeeCents,
          connectedAccountId,
          description: `Payment for Invoice ${invoice.invoice_number}`,
        }),
      });

      const { clientSecret, paymentIntentId, error: createError } = await createResponse.json();

      if (createError) {
        throw new Error(createError);
      }

      // Collect payment method from card
      setStatus('collecting');
      const { paymentIntent, error: collectError } = await terminal.collectPaymentMethod(clientSecret);

      if (collectError) {
        throw new Error(collectError.message);
      }

      // Process the payment
      setStatus('processing');
      const { paymentIntent: processedIntent, error: processError } = await terminal.processPayment(paymentIntent);

      if (processError) {
        throw new Error(processError.message);
      }

      // Capture the payment
      const captureResponse = await fetch('/api/terminal?action=capture-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentIntentId: processedIntent.id,
          companyId,
        }),
      });

      const captureResult = await captureResponse.json();

      if (captureResult.error) {
        throw new Error(captureResult.error);
      }

      setStatus('succeeded');
      showSuccess('Payment successful!');

      if (onSuccess) {
        onSuccess({
          paymentIntentId: processedIntent.id,
          amount: amountCents,
          paymentType: 'terminal',
        });
      }
    } catch (err) {
      setError(err.message);
      setStatus('failed');
      showError('Payment failed: ' + err.message);
    }
  };

  // Cancel payment collection
  const cancelPayment = async () => {
    if (terminal) {
      try {
        await terminal.cancelCollectPaymentMethod();
      } catch (err) {
      }
    }
    setStatus('idle');
    setError(null);
  };

  // If no readers available
  if (!loadingReaders && readers.length === 0) {
    return (
      <button
        disabled
        style={{
          width: '100%',
          padding: size === 'large' ? '1rem' : '0.75rem',
          background: '#3a3a3a',
          color: '#666',
          border: 'none',
          borderRadius: '8px',
          fontSize: size === 'large' ? '1rem' : '0.9rem',
          cursor: 'not-allowed',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
        }}
      >
        <Smartphone size={18} />
        No Card Readers Available
      </button>
    );
  }

  // Payment succeeded state
  if (status === 'succeeded') {
    return (
      <div
        style={{
          width: '100%',
          padding: size === 'large' ? '1rem' : '0.75rem',
          background: '#4ecca320',
          border: '1px solid #4ecca3',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          color: '#4ecca3',
          fontWeight: 'bold',
        }}
      >
        <CheckCircle size={18} />
        Payment Complete
      </div>
    );
  }

  // Reader selection modal
  if (showReaderSelect) {
    return (
      <div style={{
        background: '#2a2a2a',
        borderRadius: '8px',
        padding: '1rem',
        border: '1px solid #4ecca3',
      }}>
        <div style={{ marginBottom: '0.75rem', color: '#e0e0e0', fontWeight: 'bold' }}>
          Select Card Reader
        </div>

        {loadingReaders ? (
          <div style={{ textAlign: 'center', padding: '1rem', color: '#888' }}>
            <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {readers.map((reader) => (
              <button
                key={reader.id}
                onClick={() => connectToReader(reader)}
                disabled={status === 'connecting'}
                style={{
                  padding: '0.75rem',
                  background: '#1a1a1a',
                  border: '1px solid #3a3a3a',
                  borderRadius: '6px',
                  color: '#e0e0e0',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  textAlign: 'left',
                }}
              >
                {reader.status === 'online' ? (
                  <Wifi size={18} color="#4ecca3" />
                ) : (
                  <WifiOff size={18} color="#888" />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold' }}>{reader.label || 'Reader'}</div>
                  <div style={{ fontSize: '0.8rem', color: '#888' }}>
                    {reader.device_type}
                  </div>
                </div>
                <span style={{
                  fontSize: '0.75rem',
                  padding: '0.2rem 0.5rem',
                  borderRadius: '4px',
                  background: reader.status === 'online' ? '#4ecca320' : '#3a3a3a',
                  color: reader.status === 'online' ? '#4ecca3' : '#888',
                }}>
                  {reader.status}
                </span>
              </button>
            ))}

            {/* Simulated reader option for testing */}
            <button
              onClick={() => connectToReader({
                id: 'tmr_simulated',
                label: 'Simulated Reader (Test)',
                device_type: 'simulated',
                status: 'online',
              })}
              disabled={status === 'connecting'}
              style={{
                padding: '0.75rem',
                background: '#1a1a1a',
                border: '1px dashed #f39c12',
                borderRadius: '6px',
                color: '#f39c12',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                textAlign: 'left',
                fontSize: '0.85rem',
              }}
            >
              <CreditCard size={18} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 'bold' }}>Use Simulated Reader</div>
                <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                  For testing without hardware
                </div>
              </div>
            </button>
          </div>
        )}

        <button
          onClick={() => setShowReaderSelect(false)}
          style={{
            marginTop: '0.75rem',
            width: '100%',
            padding: '0.5rem',
            background: 'transparent',
            border: '1px solid #3a3a3a',
            borderRadius: '6px',
            color: '#888',
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>

        {status === 'connecting' && (
          <div style={{
            marginTop: '0.75rem',
            padding: '0.75rem',
            background: '#1a1a1a',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#4ecca3',
          }}>
            <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
            Connecting to reader...
          </div>
        )}

        {error && (
          <div style={{
            marginTop: '0.75rem',
            padding: '0.75rem',
            background: '#ff6b6b20',
            borderRadius: '6px',
            color: '#ff6b6b',
            fontSize: '0.85rem',
          }}>
            {error}
          </div>
        )}
      </div>
    );
  }

  // Main button states
  const isProcessing = ['connecting', 'collecting', 'processing'].includes(status);

  return (
    <div>
      <button
        onClick={collectPayment}
        disabled={isProcessing || loadingReaders}
        style={{
          width: '100%',
          padding: size === 'large' ? '1rem' : '0.75rem',
          background: isProcessing ? '#3a3a3a' : 'linear-gradient(135deg, #4ecca3, #45b393)',
          color: isProcessing ? '#888' : '#000',
          border: 'none',
          borderRadius: '8px',
          fontSize: size === 'large' ? '1rem' : '0.9rem',
          fontWeight: 'bold',
          cursor: isProcessing ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
        }}
      >
        {loadingReaders ? (
          <>
            <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
            Loading...
          </>
        ) : status === 'collecting' ? (
          <>
            <Smartphone size={18} />
            Present Card...
          </>
        ) : status === 'processing' ? (
          <>
            <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
            Processing...
          </>
        ) : connectedReader ? (
          <>
            <Smartphone size={18} />
            Tap/Insert Card - {formatCurrency(invoice.balance_due * 100)}
          </>
        ) : (
          <>
            <Smartphone size={18} />
            Pay with Card Reader - {formatCurrency(invoice.balance_due * 100)}
          </>
        )}
      </button>

      {isProcessing && (
        <button
          onClick={cancelPayment}
          style={{
            marginTop: '0.5rem',
            width: '100%',
            padding: '0.5rem',
            background: 'transparent',
            border: '1px solid #ff6b6b',
            borderRadius: '6px',
            color: '#ff6b6b',
            cursor: 'pointer',
            fontSize: '0.85rem',
          }}
        >
          Cancel
        </button>
      )}

      {connectedReader && status === 'idle' && (
        <div style={{
          marginTop: '0.5rem',
          fontSize: '0.8rem',
          color: '#4ecca3',
          textAlign: 'center',
        }}>
          Connected: {connectedReader.label || 'Reader'}
        </div>
      )}

      {error && status === 'failed' && (
        <div style={{
          marginTop: '0.5rem',
          padding: '0.75rem',
          background: '#ff6b6b20',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}>
          <XCircle size={16} color="#ff6b6b" />
          <span style={{ color: '#ff6b6b', fontSize: '0.85rem' }}>{error}</span>
        </div>
      )}
    </div>
  );
}
